import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {NewGameService} from "../../../services/new-game.service";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {IGameAssetSettingsForm} from "../../../interfaces/new-game/forms/IGameAssetSettingsForm";
import {MatError, MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatToolbar} from "@angular/material/toolbar";
import {MatIcon} from "@angular/material/icon";
import {NgxColorsModule} from "ngx-colors";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {GameThemeType} from "../../../enum/GameThemeType";
import {IUploadImageToAzure} from "../../../interfaces/IUploadImageToAzure";
import {ActiveUserService} from "../../../services/active-user.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {CreateImageState} from "../../../enum/CreateImageState";
import {AssetManagementService} from "../../../services/asset-management.service";
import {MatInput} from "@angular/material/input";
import {NgIf} from "@angular/common";

@Component({
  selector: 'app-game-asset-settings-form',
  standalone: true,
  templateUrl: './game-asset-settings-form.component.html',
  imports: [
    MatLabel,
    ReactiveFormsModule,
    MatToolbar,
    MatIcon,
    MatIconButton,
    MatFormField,
    NgxColorsModule,
    MatOption,
    MatSelect,
    MatButton,
    MatInput,
    MatError,
    MatHint,
    NgIf
  ],
  styleUrl: './game-asset-settings-form.component.scss'
})
export class GameAssetSettingsFormComponent implements OnInit {

  protected readonly GameThemeType = GameThemeType;
  protected readonly CreateImageState = CreateImageState;

  private newGameService: NewGameService = inject(NewGameService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  public assetManagementService: AssetManagementService = inject(AssetManagementService);

  public qrCodeImageState: CreateImageState = CreateImageState.NoImageSaved;
  public gameLogoImageState: CreateImageState = CreateImageState.NoImageSaved;

  private urlRegex: RegExp = /^(https?:\/\/)?([a-zA-Z0-9_-]+\.)*[a-zA-Z0-9][a-zA-Z0-9_-]+\.[a-zA-Z]{2,9}(\/.*)?$/;
  private charityUrlRegex: RegExp = /^https:\/\/.+/;

  @Input() newGameAssetsSettingsForm: FormGroup<IGameAssetSettingsForm> = new FormGroup<IGameAssetSettingsForm>({
    qrCodeImage: new FormControl<string>('', Validators.pattern(this.urlRegex)),
    primaryGameColor: new FormControl<string>('', Validators.required),
    gameThemeTypeId: new FormControl<number>(0, Validators.required),
    charityRegistrationUrl: new FormControl<string | null>(null, Validators.pattern(this.charityUrlRegex)),
  });

  @Output() newGameAssetsSettingsFormChange: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  ngOnInit(): void {
    this.populateGameDrawFromState();
    this.checkForExistingGameAssets();
  }

  private checkForExistingGameAssets(): void {
    this.assetManagementService.checkForExistingGameLogo(this.newGameService.newGameSubdomain()!);
    this.assetManagementService.checkForExistingQrCodeImage(this.newGameService.newGameSubdomain()!);
  }

  onThemeTypeChange(valueP: GameThemeType) {
    this.assetManagementService.handleThemeTypeChange(valueP);
  }

  onFormChange() {
    this.newGameAssetsSettingsFormChange.emit(this.newGameAssetsSettingsForm)
  }

  onQrCodeFileSelected(event: any) {
    this.assetManagementService.handleQrCodeSelected(event);
  }

  onGameLogoFileSelected(event: any) {
    this.assetManagementService.handleGameLogoSelected(event);
  }

  editGameLogo() {
    this.assetManagementService.gameLogoUrl = '';
    this.gameLogoImageState = CreateImageState.NoImageSaved;
  }

  editQRCode() {
    this.assetManagementService.qrCodeImageUrl = '';
    this.qrCodeImageState = CreateImageState.NoImageSaved;
  }

  saveQRCode() {
    const formData = new FormData();

    if (this.assetManagementService.qrCodeImage) {
      formData.append('file', this.assetManagementService.qrCodeImage, 'qrcode.png');

      const new_logo_request: IUploadImageToAzure = {
        gameId: this.newGameService.newGameId()!,
        image: formData,
        adminId: this.activeUserService.activeUser().Id
      };

      this.assetManagementService.uploadGameImage(new_logo_request).subscribe({
        next: (res: any) => {
          if (res.status == 200) {
            this.snackbarService.openSuccessfulSnackBar('Successfully uploaded Game QR Code.');
            this.checkForExistingGameAssets();
          }
        },
        error: () => {
          this.snackbarService.openErrorSnackBar('Failed to upload Game QR Code.');
        }
      });
    }
  }

  saveGameLogo() {
    const formData = new FormData();

    if (this.assetManagementService.gameLogoImage) {
      formData.append('file', this.assetManagementService.gameLogoImage, 'game-logo.svg');

      const new_logo_request: IUploadImageToAzure = {
        gameId: this.newGameService.newGameId()!,
        image: formData,
        adminId: this.activeUserService.activeUser().Id
      };

      this.assetManagementService.uploadGameImage(new_logo_request).subscribe({
        next: (res: any) => {
          if (res.status == 200) {
            this.snackbarService.openSuccessfulSnackBar('Successfully uploaded Game Logo.');
            this.checkForExistingGameAssets();
          }
        },
        error: () => {
          this.snackbarService.openErrorSnackBar('Failed to upload Game Logo.');
        }
      });

    }
  }

  private populateGameDrawFromState() {
    const newGameDrawFromState = this.newGameService.getNewGameRequest()?.newGameAssets;
    if (newGameDrawFromState) {

      this.newGameAssetsSettingsForm.patchValue({
          gameThemeTypeId: newGameDrawFromState.gameThemeTypeId,
          primaryGameColor: newGameDrawFromState.primaryGameColor,
          charityRegistrationUrl: newGameDrawFromState.charityRegistrationUrl
        }
      )
    }

    this.newGameAssetsSettingsFormChange.emit(this.newGameAssetsSettingsForm)
  }

}
